import { create } from "zustand" // move to lib/states
import { persist } from "zustand/middleware"

interface OpenGroupStore {
	// Sections and capped lists in the docs nav default to open, so only the
	// ids the user changed are stored. Persisted, so a reload keeps the sidebar
	// the way it was left.
	collapsedSections: string[]
	toggleSection: (sectionId: string) => void
	openSection: (sectionId: string) => void
	expandedLists: string[]
	toggleList: (listId: string) => void

	openGroupIds: string[]
	manuallyClosedGroups: string[]
	openGroup: (groupId: string) => void
	closeGroup: (groupId: string) => void
	toggleGroup: (groupId: string) => void
	clearOpenGroups: () => void
	isGroupOpen: (groupId: string) => boolean

	openArticleIds: string[]
	manuallyClosedArticles: string[]
	openArticle: (articleId: string) => void
	toggleArticle: (articleId: string) => void
	isArticleOpen: (articleId: string) => boolean

	isAddGroupOpen: boolean
	setIsAddGroupOpen: (isOpen: boolean) => void

	setIsNavOpen: (isOpen: boolean) => void
	isNavOpen: boolean

	lastOpenedArticleId: string
	isArrowNavigation: boolean
	setIsArrowNavigation: (isArrow: boolean) => void
}

export const useDocNavStore = create<OpenGroupStore>()(
	persist(
		(set, get) => ({
			collapsedSections: [],
			toggleSection: (sectionId: string) =>
				set((state) => ({
					collapsedSections: state.collapsedSections.includes(sectionId)
						? state.collapsedSections.filter((id) => id !== sectionId)
						: [...state.collapsedSections, sectionId],
				})),
			openSection: (sectionId: string) =>
				set((state) => ({
					collapsedSections: state.collapsedSections.filter(
						(id) => id !== sectionId,
					),
				})),

			expandedLists: [],
			toggleList: (listId: string) =>
				set((state) => ({
					expandedLists: state.expandedLists.includes(listId)
						? state.expandedLists.filter((id) => id !== listId)
						: [...state.expandedLists, listId],
				})),

			// Group navigation state
			openGroupIds: [],
			manuallyClosedGroups: [],
			openGroup: (groupId: string) =>
				set((state) => ({
					openGroupIds: state.openGroupIds.includes(groupId)
						? state.openGroupIds
						: [...state.openGroupIds, groupId],
					manuallyClosedGroups: state.manuallyClosedGroups.filter(
						(id) => id !== groupId,
					),
				})),

			closeGroup: (groupId: string) =>
				set((state) => ({
					openGroupIds: state.openGroupIds.filter((id) => id !== groupId),
					manuallyClosedGroups: [...state.manuallyClosedGroups, groupId],
				})),

			toggleGroup: (groupId: string) =>
				set((state) => {
					const isCurrentlyOpen = state.openGroupIds.includes(groupId)
					return {
						openGroupIds: isCurrentlyOpen
							? state.openGroupIds.filter((id) => id !== groupId)
							: [...state.openGroupIds, groupId],
						manuallyClosedGroups: isCurrentlyOpen
							? [...state.manuallyClosedGroups, groupId]
							: state.manuallyClosedGroups.filter((id) => id !== groupId),
					}
				}),

			clearOpenGroups: () => set({ openGroupIds: [] }),

			isGroupOpen: (groupId: string) => get().openGroupIds.includes(groupId),

			openArticleIds: [],
			manuallyClosedArticles: [],
			openArticle: (articleId: string) =>
				set((state) => ({
					openArticleIds: state.openArticleIds.includes(articleId)
						? state.openArticleIds
						: [...state.openArticleIds, articleId],
					manuallyClosedArticles: state.manuallyClosedArticles.filter(
						(id) => id !== articleId,
					),
				})),
			toggleArticle: (articleId: string) =>
				set((state) => {
					const isCurrentlyOpen = state.openArticleIds.includes(articleId)
					return {
						openArticleIds: isCurrentlyOpen
							? state.openArticleIds.filter((id) => id !== articleId)
							: [...state.openArticleIds, articleId],
						manuallyClosedArticles: isCurrentlyOpen
							? [...state.manuallyClosedArticles, articleId]
							: state.manuallyClosedArticles.filter((id) => id !== articleId),
					}
				}),
			isArticleOpen: (articleId: string) =>
				get().openArticleIds.includes(articleId),

			// Group creator state
			isAddGroupOpen: false,
			setIsAddGroupOpen: (isOpen: boolean) => set({ isAddGroupOpen: isOpen }),

			//Mobile navigation state
			isNavOpen: false,
			setIsNavOpen: (isOpen: boolean) => set({ isNavOpen: isOpen }),

			// Article navigation state
			lastOpenedArticleId: "",

			// Arrow navigation state
			isArrowNavigation: false,
			setIsArrowNavigation: (isArrow: boolean) =>
				set({ isArrowNavigation: isArrow }),
		}),
		{
			name: "docs-nav-state",
			partialize: (state) => ({
				collapsedSections: state.collapsedSections,
				expandedLists: state.expandedLists,
			}),
		},
	),
)

export const DOCS_SECTIONS = {
	suggestions: "suggestions",
	public: "public",
	agent: "agent",
	internal: "internal",
	sources: "sources",
} as const

export type DocsSectionId = (typeof DOCS_SECTIONS)[keyof typeof DOCS_SECTIONS]

export const useIsDocsSectionOpen = (sectionId: DocsSectionId) =>
	useDocNavStore((s) => !s.collapsedSections.includes(sectionId))

export const useIsDocsListExpanded = (listId: DocsSectionId) =>
	useDocNavStore((s) => s.expandedLists.includes(listId))
